import gsap from 'gsap';
import Magnetic from "includes/_1_magnetic";

export default class Cursor {
    constructor(options = {}) {
        this.options = {
            container: "body",
            speed: 0.7,
            ease: "expo.out",
            visibleTimeout: 300,
            ...options
        };
        this.body = document.querySelector(this.options.container);
        this.el = document.createElement('div');
        this.el.className = 'cursor';
        this.text = document.createElement('div');
        this.text.className = 'cursor-text';
        this.scrollContainer = this.getScrollContainer();
        this.mountRoot = this.getMountRoot();
        this.init();
    }

    init() {
        this.el.append(this.text);
        this.mountRoot.appendChild(this.el);
        this.bind();
        this.move(-window.innerWidth, -window.innerHeight, 0);
    }

    getScrollContainer() {
        return document.querySelector('.js-reading-content') || document.querySelector('.off-canvas__main') || window;
    }

    getMountRoot() {
        return this.scrollContainer === window ? document.body : this.scrollContainer;
    }

    getPointerPosition(clientX, clientY) {
        if (this.stick) {
            return {
                x: this.stick.x - ((this.stick.x - clientX) * 0.15),
                y: this.stick.y - ((this.stick.y - clientY) * 0.15)
            };
        }

        if (this.scrollContainer === window) {
            return {
                x: clientX + window.scrollX,
                y: clientY + window.scrollY
            };
        }

        const rect = this.mountRoot.getBoundingClientRect();

        return {
            x: clientX - rect.left + this.mountRoot.scrollLeft,
            y: clientY - rect.top + this.mountRoot.scrollTop
        };
    }

    bind() {
        // Direct event listeners
        this.body.addEventListener('mouseleave', () => {
            this.hide();
        });

        this.body.addEventListener('mouseenter', () => {
            this.show();
        });

        document.addEventListener('mousemove', (e) => {
            this.clientPos = { x: e.clientX, y: e.clientY };
            this.pos = this.getPointerPosition(e.clientX, e.clientY);
            this.update();
        });

        // Recalculate cursor position when the scroll context changes.
        const onScroll = () => {
            if (!this.clientPos) return;
            this.pos = this.getPointerPosition(this.clientPos.x, this.clientPos.y);
            this.update();
        };

        if (this.scrollContainer === window) {
            window.addEventListener('scroll', onScroll, { passive: true });
        } else if (this.scrollContainer) {
            this.scrollContainer.addEventListener('scroll', onScroll, { passive: true });
        }

        if (window.lenis && typeof window.lenis.on === 'function') {
            window.lenis.on('scroll', onScroll);
        }

        this.body.addEventListener('mousedown', () => {
            this.setState('-active');
        });

        this.body.addEventListener('mouseup', () => {
            this.removeState('-active');
        });

        // Delegated event listeners
        this.body.addEventListener('mouseenter', (e) => {
            const target = e.target;

            if (target.matches('iframe')) {
                this.hide();
                return;
            }

            if (target.matches('a, input, textarea, button')) {
                this.setState('-pointer');
            }

            if (target.dataset.cursor) {
                this.setState(target.dataset.cursor);
            }

            if (target.dataset.cursorText) {
                this.setText(target.dataset.cursorText);
            }

            if (target.dataset.cursorStick) {
                this.setStick(target.dataset.cursorStick);
            }
        }, true);

        this.body.addEventListener('mouseleave', (e) => {
            const target = e.target;

            if (target.matches('iframe')) {
                this.show();
                return;
            }

            if (target.matches('a, input, textarea, button')) {
                this.removeState('-pointer');
            }

            if (target.dataset.cursor) {
                this.removeState(target.dataset.cursor);
            }

            if (target.dataset.cursorText) {
                this.removeText();
            }

            if (target.dataset.cursorStick) {
                this.removeStick();
            }
        }, true);
    }

    setState(state) {
        this.parseStates(state).forEach(token => this.el.classList.add(token));
    }

    removeState(state) {
        this.parseStates(state).forEach(token => this.el.classList.remove(token));
    }

    parseStates(state) {
        if (!state || typeof state !== 'string') return [];
        return state
            .split(' ')
            .map(token => token.trim())
            .filter(Boolean);
    }

    toggleState(state) {
        this.el.classList.toggle(state);
    }

    setText(text) {
        this.text.innerHTML = text;
        this.el.classList.add('-text');
    }

    removeText() {
        this.el.classList.remove('-text');
    }

    setStick(selector) {
        const target = document.querySelector(selector);
        if (!target) return;
        
        const bound = target.getBoundingClientRect();
        const position = this.getPointerPosition(
            bound.left + (target.offsetWidth / 2),
            bound.top + (target.offsetHeight / 2)
        );

        this.stick = {
            x: position.x,
            y: position.y
        };

        this.move(this.stick.x, this.stick.y, 5);
    }

    removeStick() {
        this.stick = false;
    }

    update() {
        this.move();
        this.show();
    }

    move(x, y, duration) {
        const targetX = x ?? this.pos?.x ?? 0;
        const targetY = y ?? this.pos?.y ?? 0;
        gsap.to(this.el, {
            left: targetX,
            top: targetY,
            overwrite: true,
            ease: this.options.ease,
            duration: this.visible ? (duration ?? this.options.speed) : 0
        });
    }

    show() {
        if (this.visible) return;
        clearInterval(this.visibleInt);
        this.el.classList.add('-visible');
        this.visibleInt = setTimeout(() => this.visible = true);
    }

    hide() {
        clearInterval(this.visibleInt);
        this.el.classList.remove('-visible');
        this.visibleInt = setTimeout(() => this.visible = false, this.options.visibleTimeout);
    }
}


const supportsFinePointer = () => {
    if (!window.matchMedia) return false;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
};

if (supportsFinePointer()) {
    new Cursor();

    document.querySelectorAll('[data-magnetic]').forEach(el => {
        new Magnetic(el);
    });
}
